import PropTypes from 'prop-types'
import React from 'react'

import AttachmentsPopover from './AttachmentsPopover'
import { savesInPlace } from './queue'
import { DealerClosedSwitch, RowSaveButton, StatusChips } from './RowControls'

/**
 * The cells every Work Queue row ends with (WARP-966): Change Status where the tab has
 * moves, Dealer Closed, Files, and the row's Save, with `children` (an Open link) beside
 * Save. The row's own columns come first; the page's header lists the same columns.
 *
 * `permissions` say what this user may do (see `rowPermissions`). A user who may not
 * change status gets no status or flag cells at all, rather than disabled controls.
 */
const RowActionCells = ({
  children,
  edit,
  onChange,
  onSave,
  options,
  permissions,
  row,
  saving,
}) => {
  const { canAttach, canChangeStatus, canRemoveFiles } = permissions

  return (
    <>
      {canChangeStatus && options.length > 0 && (
        <td className="p-3">
          <StatusChips
            choice={edit.disposition}
            disabled={saving}
            name={`disposition-${row.id}`}
            onChoose={(disposition) => onChange({ disposition })}
            options={options}
            roNumber={row.roNumber}
          />
        </td>
      )}

      {canChangeStatus && (
        <td className="p-3">
          <DealerClosedSwitch
            checked={edit.dealerClosed}
            disabled={saving}
            onChange={(dealerClosed) => onChange({ dealerClosed })}
            roNumber={row.roNumber}
          />
        </td>
      )}

      <td className="p-3">
        <AttachmentsPopover
          canAdd={canAttach}
          canRemove={canRemoveFiles}
          disabled={saving}
          files={row.files}
          filesToDelete={edit.filesToDelete}
          newFiles={edit.newFiles}
          onChange={onChange}
          roNumber={row.roNumber}
        />
      </td>

      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          {(canChangeStatus || canAttach) && (
            <RowSaveButton
              changed={savesInPlace(row, edit)}
              onSave={onSave}
              roNumber={row.roNumber}
              saving={saving}
            />
          )}
          {children}
        </div>
      </td>
    </>
  )
}

RowActionCells.propTypes = {
  children: PropTypes.node,
  edit: PropTypes.shape({
    dealerClosed: PropTypes.bool,
    disposition: PropTypes.string,
    filesToDelete: PropTypes.arrayOf(PropTypes.string),
    newFiles: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  // The statuses this row can move to, `[{ value, label }]`; empty for none.
  options: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  ),
  permissions: PropTypes.shape({
    canAttach: PropTypes.bool,
    canChangeStatus: PropTypes.bool,
    canRemoveFiles: PropTypes.bool,
  }).isRequired,
  row: PropTypes.object.isRequired,
  saving: PropTypes.bool,
}

RowActionCells.defaultProps = {
  children: null,
  options: [],
  saving: false,
}

export default RowActionCells
